import { createAsyncThunk } from '@reduxjs/toolkit';

import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { AdminApiPaths } from '../../../api/APIPaths';
import { ILessonStudentFeedback, ILessonStudentFeedbackRewardData } from '.';

export type TAdminCreateLessonStudentFeedback = {
  tutor: string;
  students: string[];
  section: string;
  feedbackRewards?: string[];
  overallTutorRating?: number;
  teachingConcept?: number;
  questionPractice?: number;
  communicationSkills?: number;
  isContinueLesson?: 'Yes' | 'No';
  subjectAndSyllabusKnowledge?: number;
  punctualAndProfessional?: number;
  technicalSkills?: number;
  overallTutopiyaRating?: number;
  feedbackComments?: string;
  // new fields
  tutorExplanationClarity?: number;
  tutorSupportResponsiveness?: number;
  tutorAttitudeFriendliness?: number;
  classInteractionEngagement?: number;
  studentConfidenceImprovement?: number;
};

export type TAdminGetLessonStudentFeedbacks = {
  limit: number;
  offset: number;
  section?: string;
};

export type TAdminUpdateLessonStudentFeedback = {
  id: string;
  feedbackRewards?: string[];
  overallTutorRating?: number;
  teachingConcept?: number;
  questionPractice?: number;
  communicationSkills?: number;
  isContinueLesson?: 'Yes' | 'No';
  subjectAndSyllabusKnowledge?: number;
  punctualAndProfessional?: number;
  technicalSkills?: number;
  overallTutopiyaRating?: number;
  feedbackComments?: string;
  // new fields
  tutorExplanationClarity?: number;
  tutorSupportResponsiveness?: number;
  tutorAttitudeFriendliness?: number;
  classInteractionEngagement?: number;
  studentConfidenceImprovement?: number;
};

const apiDataProvider = new APIDataProvider();

export const adminGetLessonStudentFeedbackRewards = createAsyncThunk(
  '/admin/lesson-student-feedbacks/feedback-rewards',
  async () => {
    const path = `${AdminApiPaths.lessonStudentFeedbacks}/feedback-rewards`;

    const response = await apiDataProvider.get<
      TApiResponse<ILessonStudentFeedbackRewardData>
    >(path);

    return response.data;
  }
);

export const adminCreateLessonStudentFeedback = createAsyncThunk(
  '/admin/lesson-student-feedbacks/create',
  async (data: TAdminCreateLessonStudentFeedback) => {
    const path = `${AdminApiPaths.lessonStudentFeedbacks}`;

    const response = await apiDataProvider.post<
      TApiResponse<ILessonStudentFeedback[]>
    >(path, data);

    return response.data;
  }
);

export const adminGetLessonStudentFeedbacks = createAsyncThunk(
  '/admin/lesson-student-feedbacks/list',
  async ({ limit, offset, section }: TAdminGetLessonStudentFeedbacks) => {
    let path = `${AdminApiPaths.lessonStudentFeedbacks}?limit=${limit}&offset=${offset}`;

    if (section) {
      path += `&section=${section}`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<TApiPaginationResponse<ILessonStudentFeedback>>
    >(path);

    return response.data;
  }
);

export const adminUpdateLessonStudentFeedbacks = createAsyncThunk(
  '/admin/lesson-student-feedbacks/update-one',
  async (data: TAdminUpdateLessonStudentFeedback) => {
    const path = `${AdminApiPaths.lessonStudentFeedbacks}/${data.id}`;

    const response = await apiDataProvider.put<
      TApiResponse<ILessonStudentFeedback>
    >(path, data);

    return response.data;
  }
);

export const adminDeleteLessonStudentFeedback = createAsyncThunk(
  '/admin/lesson-student-feedbacks/delete-one',
  async ({ id }: { id: string }) => {
    const path = `${AdminApiPaths.lessonStudentFeedbacks}/${id}`;

    const response = await apiDataProvider.delete<
      TApiResponse<ILessonStudentFeedback>
    >(path);

    return response.data;
  }
);
